/**
 * Word counts and goals on screen (part 19b log, tables B, G and Y): the toolbar's count of the open chapter (with today's
 * words of the daily goal when there is one), and the word count dialog: the chapter, the selection, the book, today's
 * words, and the book's goals. Counts are shown, never said as they change; the book's count once known, and the daily
 * goal reached, are news (principle 5).
 */
import { createSignal } from 'solid-js';
import type { Measure } from '../count/controller.ts';
import { progress, readGoal } from '../count/tally.ts';
import { el, when } from '../ui/dom.ts';
import Button from '../ui/Button.ts';
import DescriptionList from '../ui/DescriptionList.ts';
import Field from '../ui/Field.ts';
import TextField from '../ui/TextField.ts';
import { component } from '../ui/solid.ts';
import { ModalBody } from './modal.ts';
import type { Child } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** The toolbar's text: the open chapter's words (or what the button is, before a count), and today's of the daily goal. */
export function wordsLabel(shell: Shell): string {
  const { t, counter } = shell;
  const words = counter.words();
  const shown = words === null ? t('words.button') : t('words.chapter', { count: words });
  const goal = counter.goals()?.dailyGoal ?? null;
  return goal === null ? shown : `${shown} · ${t('words.today', { today: counter.today(), goal })}`;
}

/** B1: opens the word count dialog, counting the book for it. */
export function openWords(shell: Shell): void {
  shell.counter.countBook();
  shell.ui.setDialog({ kind: 'words' });
}

/** The toolbar's count (Y): a button opening the dialog, named by what it shows. */
export function WordsButton(shell: Shell): Child {
  return component(Button, {
    variant: 'ghost',
    size: 'sm',
    // The width stays put as the count changes (style.css); a daily goal adds today's words, so more room.
    get class() {
      return (shell.counter.goals()?.dailyGoal ?? null) === null ? 'open-words' : 'open-words with-goal';
    },
    'aria-haspopup': 'dialog',
    onClick: () => {
      openWords(shell);
    },
    get children() {
      return wordsLabel(shell);
    },
  });
}

/** B2–B4: what the book's line says. */
export function bookText(shell: Pick<Shell, 't'>, measure: Measure): string {
  const { t } = shell;
  switch (measure.kind) {
    case 'idle':
    case 'counting':
      return t('words.counting');
    case 'failed':
      return t('words.failed');
    case 'counted':
      return measure.unreadable === 0 ? t('words.count', { count: measure.book }) : `${t('words.count', { count: measure.book })} ${t('words.unreadable', { count: measure.unreadable })}`;
  }
}

/** B2, B4: the dialog's news: the book's count once known, or that counting failed; nothing while it counts. */
export function measureNews(shell: Pick<Shell, 't'>, measure: Measure): string {
  if (measure.kind === 'failed') return shell.t('words.failed');
  if (measure.kind !== 'counted') return '';
  const counted = shell.t('words.counted', { count: measure.book });
  return measure.unreadable === 0 ? counted : `${counted} ${shell.t('words.unreadable', { count: measure.unreadable })}`;
}

/** B2, B4: the news of the dialog's counts now. */
export function wordsNews(shell: Shell): string {
  return measureNews(shell, shell.counter.measure());
}

/** G6: the daily goal reached, said once. */
export function goalNews(shell: Shell): string {
  const news = shell.counter.news();
  return news === null ? '' : shell.t('words.reached', { goal: news.goal });
}

/**
 * G1–G5: a goal's field (the library's TextField, its label beside the box): its text (the goal stored, or what the
 * writer typed), stored as typed whenever it reads (G2); a value that doesn't read is the field's error once the writer
 * leaves the field or presses Enter (G3), the goal kept. Its hint is the field's help; its progress follows it.
 */
export function GoalField(shell: Shell, which: 'bookGoal' | 'dailyGoal', label: string, reached: () => string, id = `goal-${which}`): Child {
  const { t, counter } = shell;
  const stored = counter.goals()?.[which] ?? null;
  const [refused, setRefused] = createSignal(false);
  const check = (text: string): void => {
    setRefused(!counter.setGoal(which, text));
  };
  return el(
    'div',
    { class: 'goal' },
    component(Field, {
      id,
      label,
      inline: true,
      help: t('words.goalHint'),
      get error() {
        return refused() ? t('words.goalRefused') : undefined;
      },
      get children() {
        return component(TextField, {
          inputmode: 'numeric',
          autocomplete: 'off',
          defaultValue: stored === null ? '' : String(stored),
          // G2: stored as typed whenever it reads; a value that doesn't read is said only on leaving (G3).
          onChange: (value: string) => {
            if (readGoal(value) !== 'refused') check(value);
          },
          onCommit: check,
        });
      },
    }),
    when(
      () => reached() !== '',
      () => el('p', { class: 'progress' }, reached),
    ),
  );
}

/** Table B: the dialog's body: the counts, the goals (Close is the modal's, part 11g). */
export function WordsView(shell: Shell): Child {
  const { t, counter } = shell;
  const counted = (): Extract<Measure, { kind: 'counted' }> | null => {
    const now = counter.measure();
    return now.kind === 'counted' ? now : null;
  };
  const words = (count: number): string => t('words.count', { count });
  // G5: each goal's progress, once there is a goal and a count to measure against it.
  const bookProgress = (): string => {
    const goal = counter.goals()?.bookGoal ?? null;
    const book = counted()?.book;
    return goal === null || book === undefined ? '' : t('words.progress', { value: book, goal, percent: progress(book, goal) });
  };
  const dailyProgress = (): string => {
    const goal = counter.goals()?.dailyGoal ?? null;
    return goal === null ? '' : t('words.progress', { value: counter.today(), goal, percent: progress(counter.today(), goal) });
  };
  return [
    ModalBody(
      {},
      component(DescriptionList, {
        class: 'word-counts',
        get items() {
          const now = counter.measure();
          const chapter = now.kind === 'counted' ? now.chapter : null;
          const selection = now.kind === 'counted' ? now.selection : null;
          return [
            // B6: with no chapter open, no chapter or selection lines; B1: none for the selection with nothing selected.
            ...(chapter === null ? [] : [{ term: t('words.thisChapter'), value: words(chapter) }]),
            ...(selection === null ? [] : [{ term: t('words.selection'), value: words(selection) }]),
            { term: t('words.book'), value: bookText(shell, now) },
            { term: t('words.todayLabel'), value: words(counter.today()) },
          ];
        },
      }),
      el('h3', {}, t('words.goals')),
      el('p', {}, t('words.goalsNote')),
      GoalField(shell, 'bookGoal', t('words.bookGoal'), bookProgress),
      GoalField(shell, 'dailyGoal', t('words.dailyGoal'), dailyProgress),
    ),
  ];
}
